<script setup lang="ts">
import type { THeroBannerBackdrop } from './types'

// Abstract background on the right side of the hero; the text side stays clean
const props = defineProps<{
  variant: THeroBannerBackdrop
}>()

const tundukOffsets = [-34, 0, 34]
const rings = [60, 105, 150, 195, 240]
const waves = Array.from({ length: 9 }, (_, index) => index)
</script>

<template>
  <div class="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <!-- Tunduk, the yurt crown from the national flag -->
    <svg
      v-if="props.variant === 'tunduk'"
      viewBox="0 0 300 300"
      fill="none"
      class="absolute -right-24 top-1/2 size-96 -translate-y-1/2"
    >
      <circle cx="150" cy="150" r="146" class="fill-white/70 stroke-blue-200" stroke-width="2" />
      <circle cx="150" cy="150" r="118" class="stroke-blue-100" stroke-width="2" />
      <g class="stroke-blue-200" stroke-width="2">
        <path
          v-for="offset in tundukOffsets"
          :key="`left-${offset}`"
          :d="`M ${90 + offset} 30 Q ${150 + offset * 2} 150 ${90 + offset} 270`"
        />
        <path
          v-for="offset in tundukOffsets"
          :key="`right-${offset}`"
          :d="`M ${210 + offset} 30 Q ${150 + offset * 2} 150 ${210 + offset} 270`"
        />
      </g>
    </svg>

    <!-- Ripples spreading from a small sun -->
    <svg
      v-else-if="props.variant === 'rings'"
      viewBox="0 0 520 520"
      fill="none"
      class="absolute -right-40 top-1/2 size-[32rem] -translate-y-1/2"
    >
      <circle
        v-for="(radius, index) in rings"
        :key="radius"
        cx="260"
        cy="260"
        :r="radius"
        class="stroke-blue-200"
        :stroke-opacity="1 - index * 0.15"
        stroke-width="2"
      />
      <circle cx="260" cy="260" r="26" class="fill-yellow-200" />
    </svg>

    <!-- Dot grid fading towards the text -->
    <svg
      v-else-if="props.variant === 'dots'"
      class="absolute inset-y-0 right-0 h-full w-3/5 [mask-image:linear-gradient(to_left,black_30%,transparent)]"
    >
      <defs>
        <pattern id="hero-backdrop-dots" width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="2" class="fill-blue-200" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hero-backdrop-dots)" />
    </svg>

    <!-- Topographic lines -->
    <svg
      v-else-if="props.variant === 'waves'"
      viewBox="0 0 600 360"
      preserveAspectRatio="none"
      fill="none"
      class="absolute inset-y-0 right-0 h-full w-3/5 [mask-image:linear-gradient(to_left,black_45%,transparent)]"
    >
      <path
        v-for="index in waves"
        :key="index"
        :d="`M 0 ${40 + index * 36} C 150 ${index * 36 - 10}, 300 ${100 + index * 36}, 600 ${20 + index * 36}`"
        class="stroke-blue-200"
        stroke-width="2"
      />
    </svg>

    <!-- Soft colour mesh -->
    <div v-else-if="props.variant === 'blobs'" class="absolute inset-y-0 right-0 w-3/5">
      <div class="absolute -right-10 -top-16 size-80 rounded-full bg-blue-200/70 blur-3xl" />
      <div class="absolute -bottom-20 right-48 size-72 rounded-full bg-yellow-200/80 blur-3xl" />
      <div class="absolute right-24 top-20 size-56 rounded-full bg-white blur-2xl" />
    </div>

    <!-- Flat geometric shapes -->
    <div v-else-if="props.variant === 'shapes'" class="absolute inset-y-0 right-0 w-[28rem]">
      <div class="absolute -right-24 -top-24 size-80 rounded-full bg-blue-100" />
      <div class="absolute -bottom-16 right-56 size-44 rounded-[2.5rem] bg-blue-100/80 rotate-12" />
      <div class="absolute bottom-10 -right-6 size-28 rounded-tl-full bg-yellow-200" />
      <div class="absolute left-6 top-8 size-5 rounded-full border-4 border-blue-200" />
    </div>
  </div>
</template>
